// React & Vendor Libs
const { useState, useMemo, useCallback, useEffect } = wp.element;

// NekoUI
import { NekoBlock, NekoWrapper, NekoColumn, NekoCheckbox, NekoTable, NekoButton, NekoQuickLinks,
  NekoLink, NekoTheme, NekoPaging, NekoTypo, NekoIcon, NekoSwitch } from '@neko-ui';
import { useKeyboardEvent } from '@neko-ui';

import useDatabaseCleanerContext, { dbSizesColumns, dbSizesColumnsForEasy, tablesFilters as filters, hideUsedByWordPressLabel, modeOptions } from '../context';
import { formatMBytes } from '../helper';
import i18n from '../i18n';
import { SectionButtonStyle, limitSelectElement, SectionFooterStyle,
  BulkUsedByButton, FilterSearchField, UsedByField, NameField } from './common';
import { useShiftSelectable } from './hooks';

const actionModes = {
  on: {
    value: 'clean',
    label: 'Clean',
  },
  off: {
    value: 'optimize',
    label: 'Optimize',
  },
};

const usedByKey = 'table_usedby';
const SettingsGeneral = () => {

  const { busy, filteredTables, tablesTotal: total, busyAction, tablesFilters, tablesUsedBy, plugins, mode } = useDatabaseCleanerContext();
  const { toggleModal, deleteTables, optimizeTables, repairTables, mutateDbSizes, updateOption,
    setSelectedUsedBy, updateTablesFilters, getTable, setTable } = useDatabaseCleanerContext();

  const [ selectedTables, setSelectedTables ] = useState([]);
  const [ busyReload, setBusyReload ] = useState(false);
  const [ actionMode, setActionMode ] = useState(actionModes.on.value);

  const isTablesBusy = busyAction.some(v => v.item === 'tables');
  const isTablesDeleteBusy = busyAction.some(v => v.item?.includes('delete_'));
  const isTablesOptimizeBusy = busyAction.some(v => v.item?.includes('optimize_'));
  const isTablesRepairBusy = busyAction.some(v => v.item?.includes('repair_'));
  const isBulkTaskBusy = isTablesDeleteBusy || isTablesOptimizeBusy || isTablesRepairBusy;
  const isBulkDeleteDisabled = selectedTables.some(v => v.info.status === 'ok');
  const allChecked = selectedTables.length === filteredTables.length;

  const { pressShift } = useKeyboardEvent();
  const { onCheckboxChange } = useShiftSelectable({
    pressShift,
    list: filteredTables,
    selectedList: selectedTables,
    setSelectedList: setSelectedTables,
    key: 'tableName'
  });

  const data = useMemo(() => filteredTables.map(v => {
    const { table: tableWithSuffix, tableName } = v;
    const checked = selectedTables.some(table => table.name === tableName);
    const isDeleteBusy = busyAction.some(busy => busy.item === `delete_${tableName}`);
    const isOptimizeBusy = busyAction.some(busy => busy.item === `optimize_${tableName}`);
    const isRepairBusy = busyAction.some(busy => busy.item === `repair_${tableName}`);
    const isActionBusy = isOptimizeBusy || isDeleteBusy || isRepairBusy;
    const isLoadingTable = busyAction.some(busy => busy.item === `loading_${tableName}`);
    const isUsed = v.info?.status === 'ok';

    const onChange = (isChecked, id) => onCheckboxChange(isChecked, v);
    const onDelete = () => toggleModal({modal: 'deleteAll', enable: true, onNext: async () => await deleteTables({ tables: [tableName] })});
    const onOptimize = async () => await optimizeTables({ tables: [tableName] });
    const onRepair = async () => await repairTables({ tables: [tableName] });
    const percentage = v?.percent?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      useGrouping: false
    }) ?? "0";

    const onEyeButtonClicked = async () => {
      setTable([]);
      const result = await getTable({ table: tableName });
      if (!result) return;
      toggleModal({modal: 'table', enable: true, title: `Table: ${tableWithSuffix}`});
    }

    return {
      deletion: <NekoButton title={i18n.INFO_TABLE_DROP}
          className={isUsed ? 'primary' : 'danger'} busyIconSize="100%"
          onClick={onDelete} icon="close" style={{ minWidth: 40, width: 40 }}
          isBusy={isDeleteBusy}
          disabled={isUsed || isOptimizeBusy || isRepairBusy}>
        </NekoButton>,
      table: <NameField id={tableWithSuffix} item={v} itemText={tableWithSuffix} onChange={onChange} checked={checked}
        isBusy={checked && isActionBusy}
      />,
      usedBy: <UsedByField item={v} itemName={tableName} itemsUsedBy={tablesUsedBy}
        optionName={usedByKey} plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
        toggleModal={toggleModal} updateOption={updateOption} onRefresh={() => { onRefresh(); }}
      />,
      size: <span style={{ display: 'inline-flex', justifyItem: 'center', alignItems: 'center' }}>
      <NekoTypo>{formatMBytes(v.size)}</NekoTypo>
      <NekoIcon onClick={onEyeButtonClicked} icon="search" color="#007cba"
        style={{ width: 20, marginLeft: 5, cursor: 'pointer' }} isBusy={isLoadingTable} />
    </span>,
      percent: `${percentage} %`,
      actions: <div style={{ display: 'flex', alignItems: 'center' }}>
        <NekoButton title={i18n.INFO_TABLE_OPTIMIZE} className='primary'
          onClick={onOptimize} icon="rocket-launch" isBusy={isOptimizeBusy}
          disabled={isActionBusy}>Optimize</NekoButton>
        <NekoButton title={i18n.INFO_TABLE_REPAIR} className='primary'
          onClick={onRepair} icon="tools" isBusy={isRepairBusy}
          disabled={isActionBusy}>Repair</NekoButton>
      </div>
    }
  }), [filteredTables, selectedTables, busyAction, tablesUsedBy, onCheckboxChange, plugins, mode, actionMode]);

  const onTablesDeleteClick = useCallback(() => {
    toggleModal({modal: 'deleteAll', enable: true, onNext: async () => {
      const failedTables = await deleteTables({ tables: selectedTables.map(v => v.name) });
      const newSelectedTables = selectedTables.filter(v => failedTables.includes(v.name));
      setSelectedTables(newSelectedTables);
    }});
  }, [selectedTables]);

  const onTablesOptimizeClick = useCallback(async() => {
    const failedTables = await optimizeTables({ tables: selectedTables.map(v => v.name) });
    const newSelectedTables = selectedTables.filter(v => failedTables.includes(v.name));
    setSelectedTables(newSelectedTables);
  }, [selectedTables]);

  const onTablesRepairClick = useCallback(async() => {
    const failedTables = await repairTables({ tables: selectedTables.map(v => v.name) });
    const newSelectedTables = selectedTables.filter(v => failedTables.includes(v.name));
    setSelectedTables(newSelectedTables);
  }, [selectedTables]);

  const columns = useMemo(() => {
    const targetColumns = mode === modeOptions.on.value ? [...dbSizesColumns] : [...dbSizesColumnsForEasy];

    // apply the action mode.
    if (actionMode === actionModes.on.value) {
      const j = targetColumns.findIndex(v => v.accessor === 'actions');
      targetColumns.splice(j, 1);
    } else if (actionMode === actionModes.off.value) {
      const k = targetColumns.findIndex(v => v.accessor === 'deletion');
      targetColumns.splice(k, 1);
    }

    const i = targetColumns.findIndex(v => v.accessor === 'table');
    const j = targetColumns.findIndex(v => v.accessor === 'actions');
    const actionsColumnExists = j !== -1;
    const k = targetColumns.findIndex(v => v.accessor === 'deletion');
    const deletionColumnExists = k !== -1;

    const onChange = (checked) => {
      setSelectedTables(checked ? filteredTables.map(v => {
        return { name: v.tableName, info: v.info };
      }) : [])
    }
    targetColumns[i].title = (<><NekoCheckbox id="all-tables" checked={allChecked} onChange={onChange} />Table</>);
    if (actionsColumnExists) {
      targetColumns[j].title = '';
    }
    if (deletionColumnExists) {
      targetColumns[k].title = '';
    }

    if (!selectedTables.length) {
      return targetColumns;
    }

    if (actionsColumnExists) {
      targetColumns[j].title = (<>
        <NekoButton icon="rocket-launch" title={i18n.INFO_TABLE_BULK_OPTIMIZE} onClick={onTablesOptimizeClick}
          isBusy={isTablesOptimizeBusy} disabled={isBulkTaskBusy}>Optimize</NekoButton>
        <NekoButton icon="tools" title={i18n.INFO_TABLE_BULK_REPAIR} onClick={onTablesRepairClick}
          isBusy={isTablesRepairBusy} disabled={isBulkTaskBusy}>Repair</NekoButton>
      </>);
    }
    if (deletionColumnExists) {
      targetColumns[k].title = (
        <NekoButton icon="close" title={i18n.INFO_TABLE_BULK_DROP} style={{ minWidth: 40, width: 40 }}
          className={!isBulkDeleteDisabled ? 'danger' : 'primary'} busyIconSize="100%" onClick={onTablesDeleteClick}
          isBusy={isTablesDeleteBusy} disabled={isBulkDeleteDisabled || isBulkTaskBusy} />
      );
    }

    return targetColumns;
  }, [mode, dbSizesColumns, dbSizesColumnsForEasy, allChecked, filteredTables, selectedTables, onTablesDeleteClick,
      onTablesOptimizeClick, isTablesDeleteBusy, isBulkDeleteDisabled, isBulkTaskBusy, isTablesOptimizeBusy, actionMode]);

  const onUpdateLimit = useCallback(async(value, name) => {
    updateOption({ param: { name, value }});
  }, [updateOption]);

  const onRefresh = useCallback(async (clearSelectedItems = false) => {
    setBusyReload(true);
    await mutateDbSizes();
    if (clearSelectedItems) {
      setSelectedTables([]);
    }
    setBusyReload(false);
  }, [mutateDbSizes]);

  const limitSelect = useMemo(() => {
    return limitSelectElement("db_historical_sizes_limit", tablesFilters.limit, (value, id) => { setSelectedTables([]); updateTablesFilters({ limit: value, page: 1 }); onUpdateLimit(value, id); });
  }, [tablesFilters.limit, updateTablesFilters, onUpdateLimit]);

  const paging = useMemo(() => {
    const { page, limit, filterBy, hideUsedByWordPress } = tablesFilters;
    const pagingTotal = total[filterBy][hideUsedByWordPress];
    return <NekoPaging currentPage={page} limit={limit} total={pagingTotal} onClick={(page) => { setSelectedTables([]); updateTablesFilters({ page }) }} />
  }, [tablesFilters.page, tablesFilters.limit, total, updateTablesFilters]);

  const currentSort = useMemo(() => {
    return { by: tablesFilters.sort, accessor: tablesFilters.accessor };
  }, [tablesFilters.accessor, tablesFilters.sort]);

  useEffect(() => {
    setSelectedTables([]);
  }, [currentSort]);

  return (
    <NekoWrapper>

      <NekoColumn minimal>
        <NekoBlock busy={busy || isTablesBusy || busyReload} title="Tables" className="primary" action={
          <div style={{ display: 'flex', justifyContent: mode === modeOptions.on.value ? 'space-between' : 'flex-end', width: '100%' }}>
            {mode === modeOptions.on.value && <NekoSwitch onLabel={actionModes.on.label} offLabel={actionModes.off.label}
              onValue={actionModes.on.value} offValue={actionModes.off.value}
              onBackgroundColor={NekoTheme.deepBlue} offBackgroundColor={NekoTheme.green} width="105px" height="30px"
              checked={actionMode === actionModes.on.value} onChange={(v) => setActionMode(v)} />}
            <BulkUsedByButton itemsUsedBy={tablesUsedBy} selectedItems={selectedTables}
            optionName='table_usedby' plugins={plugins} setSelectedUsedBy={setSelectedUsedBy}
            toggleModal={toggleModal} updateOption={updateOption} onRefresh={() => onRefresh(true)} />
          </div>
        }>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <NekoQuickLinks value={tablesFilters.filterBy} onChange={value => { setSelectedTables([]); updateTablesFilters({ filterBy: value, page: 1 }); }}>
              {filters.map(v => <NekoLink title={v.title} value={v.value} count={total[v.value][tablesFilters.hideUsedByWordPress]} variant={`${v.filterValue === 'warn' && total[v.value][tablesFilters.hideUsedByWordPress] > 0 ? 'danger' : 'primary'}`} /> )}
            </NekoQuickLinks>
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" color={NekoTheme.white} style={{...SectionButtonStyle, color: NekoTheme.blue}} onClick={onRefresh} spinning={busyReload} isBusy={busyReload} />
              {paging}
            </div>
          </div>

          <NekoTable variant="raw" alternateRowColor
            data={data} columns={columns} selectedItems={[]}
            sort={currentSort} sortIconColor={NekoTheme.blue}
            onSortChange={(accessor, sort) => updateTablesFilters({ accessor, sort })} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {limitSelect}
            {paging}
          </div>

          <div style={SectionFooterStyle}>
            <FilterSearchField id='tables_search' value={tablesFilters.search}
              onChange={(value) => updateTablesFilters({ search: value, page: 1 })}
              onReset={() => updateTablesFilters({ search: "" })}
            />
            <div style={{ flex: 'auto' }} />
            <NekoCheckbox id="tables_hide_wordpress" label={hideUsedByWordPressLabel}
              checked={tablesFilters.hideUsedByWordPress}
              onChange={(value) => updateTablesFilters({ hideUsedByWordPress: value, page: 1 })}
            />
          </div>

        </NekoBlock>
      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsGeneral;